import React from 'react';
import { ArcaPlan } from '../../constants/arcaPlans';
import { fmtNum } from '../../utils/formatArcaNumbers';
import toTitleCase from '../../utils/toTitleCase';
import { PlanReturnView, TransferReturns, todayPct, ageNote } from '../../utils/transferReturns';
import { TransferComparisonData } from '../../api/pension-plan-transfers';
import { periodLabel } from './projectionLegend';

interface ComparisonTableProps {
	/** Solo para los nombres de los planes. */
	comparisonData: TransferComparisonData;
	resolvedPlan: ArcaPlan;
	/** Rentabilidades y comisiones decididas por comparsa (`/plan-returns`). */
	returns: TransferReturns & { arca: PlanReturnView };
	/** Saldo de referencia (€) para coste/año y acumulado. */
	balance: number;
	translate: (key: string) => string;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Color del valor de la columna "Tu elección" comparado con el plan actual.
 * `higherIsBetter`: true para rentabilidad/acumulado; false para comisión/coste.
 */
const colorFor = (mine: number | null, other: number | null, higherIsBetter: boolean): string => {
	if (mine === null || other === null) return 'text-foreground';
	const a = round2(mine);
	const b = round2(other);
	if (a === b) return 'text-foreground';
	return (higherIsBetter ? a > b : a < b) ? 'text-primary' : 'text-destructive';
};

const DASH = '—';

/**
 * Tabla de comparación en una sola card: una fila por métrica (label a la izquierda) y
 * dos columnas de valores — plan actual y plan elegido. Rentabilidad, periodo y comisión de
 * gestión salen de comparsa (`/plan-returns`), la misma fuente que la gráfica, para que tabla
 * y gráfica nunca discrepen. Sin datos del plan del usuario, su columna va con «—».
 */
const ComparisonTable: React.FC<ComparisonTableProps> = ({
	comparisonData,
	resolvedPlan,
	returns,
	balance,
	translate
}) => {
	const { originPlan } = comparisonData;
	const { arca, origin, samePeriod } = returns;
	const originComm = origin?.managementFeePct ?? null;
	const arcaComm = arca.managementFeePct;
	const years = arca.periodYears;

	const pct = (n: number | null) => (n !== null ? `${fmtNum(n, 2)}%` : DASH);
	const eur = (n: number | null) => (n != null ? `${fmtNum(n, 0)} €` : DASH);
	// Sin periodo común, cada rentabilidad lleva el suyo: «15,00% (1 año)».
	// Ciclo de vida que baja con la edad: «hoy 7,83%, baja con la edad» (decisión 14/10/2026).
	const pctReturn = (plan: PlanReturnView | null) =>
		plan === null
			? DASH
			: samePeriod
				? todayPct(translate, plan, pct(plan.annualPct))
				: `${pct(plan.annualPct)} (${periodLabel(translate, plan.periodYears)})${ageNote(translate, plan)}`;
	// Rentabilidades ya netas → el acumulado no descuenta comisión otra vez.
	const accrued = (plan: PlanReturnView | null) =>
		plan && balance > 0 ? balance * (1 + plan.annualPct / 100) ** years : null;
	const cost = (fee: number | null) => (fee !== null && balance > 0 ? balance * (fee / 100) : null);

	const returnColor = colorFor(arca.annualPct, origin?.annualPct ?? null, true);
	const commColor = colorFor(arcaComm, originComm, false);

	// Label del acumulado con "neto" en negrita (i18n-safe vía {neto}) y el periodo real.
	const netWord = translate('individual.planKpi.net') || 'neto';
	const [accBefore, accAfter = ''] = (
		translate('individual.planKpi.accrued') || 'Valor {neto} acumulado a {period}'
	)
		.replace('{period}', periodLabel(translate, years))
		.split('{neto}');

	const rows: { label: React.ReactNode; current: string; chosen: string; color: string }[] = [
		{
			label: samePeriod
				? (translate('individual.planKpi.returnPeriod') || 'Rentabilidad neta a {period}').replace(
						'{period}',
						periodLabel(translate, years)
					)
				: translate('individual.planKpi.returnAnnualized') || 'Rentabilidad neta anualizada',
			current: pctReturn(origin),
			chosen: pctReturn(arca),
			color: returnColor
		},
		{
			label: translate('individual.planKpi.commission') || 'Comisión de gestión',
			current: pct(originComm),
			chosen: pct(arcaComm),
			color: commColor
		},
		{
			label: translate('individual.planKpi.annualCost') || 'Coste estimado / año',
			current: eur(cost(originComm)),
			chosen: eur(cost(arcaComm)),
			color: commColor
		},
		// Con periodos distintos los acumulados no son comparables: la fila no se pinta.
		...(samePeriod
			? [
					{
						label: (
							<>
								{accBefore}
								<strong className="font-semibold">{netWord}</strong>
								{accAfter}
							</>
						),
						current: eur(accrued(origin)),
						chosen: eur(accrued(arca)),
						color: returnColor
					}
				]
			: [])
	];

	// Nombre del PLAN de origen (no la gestora), para que el usuario identifique su plan.
	// Igual que la columna Arca, que muestra el nombre del plan. Gestora solo como fallback.
	const currentName = originPlan.name || originPlan.manager || '';
	const chosenName = toTitleCase(resolvedPlan.name);

	return (
		<div className="overflow-x-auto">
			<table className="w-full border-collapse text-sm">
				<thead>
					<tr className="align-bottom">
						<th className="w-2/5" />
						<th className="px-2 pb-2 text-right font-normal">
							<span className="block text-xs font-medium leading-tight line-clamp-2">
								{currentName}
							</span>
						</th>
						<th className="pl-2 pb-2 text-right font-normal">
							<span className="block text-xs font-medium leading-tight line-clamp-2">
								{chosenName}
							</span>
						</th>
					</tr>
				</thead>
				<tbody>
					{rows.map((row, i) => (
						<tr key={i} className="border-t border-border/40">
							<td className="py-2.5 pr-3 text-muted-foreground">{row.label}</td>
							<td className="px-2 py-2.5 text-right font-semibold tabular-nums">{row.current}</td>
							<td className={`py-2.5 pl-2 text-right font-semibold tabular-nums ${row.color}`}>
								{row.chosen}
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};

export default ComparisonTable;
